import { useEffect, useRef, useState, type ReactNode } from 'react'
import { lireSupervision, fmtHeure, type Lecture } from '../api.js'
import { nomSalle } from './salles.js'

/**
 * SUPERVISION — LE SOCLE COMMUN DES VUES (21/10/2026).
 *
 * Toutes les pages de supervision (hub, salles, appareils, incidents, réseau,
 * vidéo, audiovisuel, bâtiment, serveur, registre, réglages) lisent Sonde par
 * le relais générique de Gestion : `GET /api/v1/supervision/v/<chemin de Sonde
 * sans /v1/>` (ex. `/v/zones/R1` → `GET /v1/zones/R1`). Le corps est celui de
 * Sonde, TEL QUEL (contrat v1, `packages/contracts/src/sonde/v1/`).
 *
 * UN SEUL code couleur pour tout le site, défini ici :
 *   · ok      émeraude — en ligne, opérationnel ;
 *   · warn    ambre    — dégradé, attention ;
 *   · err     rose     — hors ligne, panne réelle constatée ;
 *   · off     neutre bleuté — éteint COMME PRÉVU (jamais une panne) ;
 *   · maint   violet   — en maintenance ;
 *   · gris    gris     — inconnu, périmé, indisponible : on ne sait pas, donc
 *                        jamais rouge, jamais vert.
 * Une valeur absente s'écrit « — », jamais 0.
 */

export type Ton = 'ok' | 'warn' | 'err' | 'off' | 'maint' | 'gris'

export { SALLES_CLUB, nomSalle, ordreSalle, renommerSalles } from './salles.js'

/** État affiché d'un appareil (`display_state`). */
export const TON_ETAT: Record<string, Ton> = {
  ONLINE: 'ok', DEGRADED: 'warn', OFFLINE: 'err', OFF_EXPECTED: 'off', MAINTENANCE: 'maint',
  UNKNOWN: 'gris', STALE: 'gris', UNAVAILABLE: 'gris',
}

export const LIBELLE_ETAT: Record<string, string> = {
  ONLINE: 'En ligne', DEGRADED: 'Dégradé', OFFLINE: 'Hors ligne', OFF_EXPECTED: 'Éteint comme prévu',
  MAINTENANCE: 'En maintenance', UNKNOWN: 'Inconnu', STALE: 'Donnée périmée', UNAVAILABLE: 'Supervision indisponible',
}

/** Niveau global ou d'une salle (`level`). */
export const TON_NIVEAU: Record<string, Ton> = { OPERATIONAL: 'ok', ATTENTION: 'warn', DEGRADED: 'err' }
export const LIBELLE_NIVEAU: Record<string, string> = { OPERATIONAL: 'Opérationnel', ATTENTION: 'Attention', DEGRADED: 'Dégradé' }

/** Situation d'alimentation d'une salle (`power.situation`). */
export const TON_ALIM: Record<string, Ton> = {
  on: 'ok', off_as_planned: 'off', off_unexpected: 'err', mains_outage: 'err', unknown: 'gris',
}

/** État d'un service de Sonde (`HEALTHY`…). */
export const TON_SERVICE: Record<string, Ton> = {
  HEALTHY: 'ok', DEGRADED: 'warn', UNAVAILABLE: 'gris', STALE: 'gris', UNKNOWN: 'gris',
}

/** Gravité d'une alerte ou d'un incident. */
export const TON_GRAVITE: Record<string, Ton> = { critical: 'err', error: 'err', warning: 'warn', info: 'gris' }
export const LIBELLE_GRAVITE: Record<string, string> = { critical: 'Critique', error: 'Erreur', warning: 'Avertissement', info: 'Information' }

/** Classe CSS d'un ton : `sv-ton--<ton>` (couleur de texte + bordure + fond léger). */
export const classeTon = (t: Ton | undefined | null): string => `sv-ton sv-ton--${t ?? 'gris'}`

/** Pastille d'état : point coloré + libellé. Le libellé de Sonde prime toujours. */
export function Pastille({ ton, children, title }: { ton: Ton | undefined | null; children: ReactNode; title?: string }) {
  return (
    <span className={classeTon(ton)} title={title}>
      <span className="sv-ton__point" aria-hidden="true" />
      <span>{children}</span>
    </span>
  )
}

/** Pastille d'un `display_state`, avec le libellé de Sonde s'il est fourni. */
export function PastilleEtat({ etat, libelle }: { etat: string | null | undefined; libelle?: string | null }) {
  const e = etat ?? 'UNKNOWN'
  return <Pastille ton={TON_ETAT[e] ?? 'gris'}>{libelle ?? LIBELLE_ETAT[e] ?? e}</Pastille>
}

/** « il y a 2 min » à partir d'un âge en secondes ; « — » si inconnu. */
export function fmtAge(s: number | null | undefined): string {
  if (s === null || s === undefined || !Number.isFinite(s)) return '—'
  if (s < 60) return `il y a ${Math.max(0, Math.round(s))} s`
  if (s < 3600) return `il y a ${Math.round(s / 60)} min`
  if (s < 86_400) return `il y a ${Math.round(s / 3600)} h`
  return `il y a ${Math.round(s / 86_400)} j`
}

/** Nombre ou « — » : jamais 0 à la place d'une valeur absente. */
export const fmtNombre = (n: number | null | undefined, unite = ''): string =>
  n === null || n === undefined || !Number.isFinite(n) ? '—' : `${n.toLocaleString('fr-FR')}${unite ? ` ${unite}` : ''}`

/** « ok / total », « — » dès qu'un des deux manque (`unobserved` jamais compté en panne). */
export const fmtRatio = (ok: number | null | undefined, total: number | null | undefined): string =>
  ok === null || ok === undefined || total === null || total === undefined ? '—' : `${ok}/${total}`

export { fmtHeure }

const objet = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)

/** Garde minimale : un corps de Sonde est un objet. Chaque page affine ensuite. */
export const estObjet = (x: unknown): x is Record<string, unknown> => objet(x)

/** Émis après une écriture réussie dans Sonde hors de la page (menu clic droit). */
export const EVENEMENT_SONDE_ECRITE = 'gestion:sonde-ecrite'

/**
 * Lecture d'une vue de Sonde, relue toutes les `intervalMs` (15 s par défaut),
 * en pause quand l'onglet est caché. `chemin` sans `/v1/` : `overview`,
 * `zones/R1`, `devices/ap-001-accueil/series?metrics=…`. La dernière lecture
 * réussie reste affichée pendant une panne passagère (`precedente`), en gris.
 */
export function useVueSonde<T>(chemin: string | null, valide: (x: unknown) => x is T, intervalMs = 15_000): {
  lecture: Lecture<T> | null
  precedente: T | null
  recharger: () => void
} {
  const [lecture, setLecture] = useState<Lecture<T> | null>(null)
  const [precedente, setPrecedente] = useState<T | null>(null)
  const [tic, setTic] = useState(0)
  const valideRef = useRef(valide)
  valideRef.current = valide

  useEffect(() => {
    if (!chemin) return
    let vivant = true
    let minuteur: ReturnType<typeof setTimeout> | null = null
    /*
     * GÉNÉRATION — une relecture lancée après une écriture (menu clic droit)
     * peut croiser une lecture encore en route. Seule la plus récente pose
     * l'état et le minuteur suivant : sinon l'ancienne réponse écraserait la
     * fraîche, et chaque croisement laisserait une boucle de lecture de plus.
     */
    let generation = 0
    const lire = async () => {
      const g = ++generation
      if (minuteur) { clearTimeout(minuteur); minuteur = null }
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        minuteur = setTimeout(() => { void lire() }, intervalMs)
        return
      }
      const l = await lireSupervision<T>(`/v/${chemin.replace(/^\/+/, '')}`, (x): x is T => valideRef.current(x))
      if (!vivant || g !== generation) return
      setLecture(l)
      if (l.etat === 'ok') setPrecedente(l.data)
      minuteur = setTimeout(() => { void lire() }, intervalMs)
    }
    void lire()
    // Un geste du menu clic droit vient d'écrire dans Sonde : on relit tout de suite.
    const apresEcriture = () => { void lire() }
    window.addEventListener(EVENEMENT_SONDE_ECRITE, apresEcriture)
    return () => { vivant = false; if (minuteur) clearTimeout(minuteur); window.removeEventListener(EVENEMENT_SONDE_ECRITE, apresEcriture) }
  }, [chemin, intervalMs, tic])

  return { lecture, precedente, recharger: () => setTic((n) => n + 1) }
}

/** Bandeau gris d'une lecture qui n'a pas abouti (jamais rouge). */
export function BandeauLecture({ lecture, depuis }: { lecture: Lecture<unknown> | null; depuis?: string | null }) {
  if (!lecture || lecture.etat === 'ok') return null
  return (
    <p className="sv-bandeau sv-bandeau--gris" role="status">
      {lecture.message}{depuis ? ` Dernière donnée : ${fmtHeure(depuis)}.` : ''}
    </p>
  )
}

/** Bandeau « Données figées depuis HH:MM » quand le collecteur de Sonde est muet. */
export function BandeauFige({ collector }: { collector: { stale?: boolean | null; ok?: boolean | null; heartbeat_age_s?: number | null; detail_fr?: string | null } | null | undefined }) {
  if (!collector || (!collector.stale && collector.ok !== false)) return null
  const depuis = typeof collector.heartbeat_age_s === 'number' ? new Date(Date.now() - collector.heartbeat_age_s * 1000).toISOString() : null
  return (
    <p className="sv-bandeau sv-bandeau--gris" role="status">
      Données figées{depuis ? ` depuis ${fmtHeure(depuis)}` : ''} : le collecteur de Sonde ne répond plus.{collector.detail_fr ? ` ${collector.detail_fr}` : ''}
    </p>
  )
}

const estZonesNommees = (x: unknown): x is { zones: { code: string; label: string }[] } =>
  objet(x) && Array.isArray(x['zones']) && (x['zones'] as unknown[]).every((z) => objet(z) && typeof z['code'] === 'string')

/**
 * Le nom affiché d'une zone à partir de son seul code (fiches, listes) :
 * « ROOM 1 » pour R1…, sinon le libellé de Sonde (relu toutes les 5 min),
 * sinon le code. `null` pour une zone absente.
 */
export function useNomsSalles(): (code: string | null | undefined) => string | null {
  const { lecture, precedente } = useVueSonde('zones', estZonesNommees, 300_000)
  const zones = (lecture?.etat === 'ok' ? lecture.data : precedente)?.zones ?? []
  return (code) => (code ? nomSalle(code, zones.find((z) => z.code === code)?.label ?? null) : null)
}
